import React, { useState, useEffect } from 'react';
import ModelSelector from './ModelSelector';
import { Chat, AIModel, User } from '../types';
import { useModal } from '../context/ModalContext';
import { useNavigate } from 'react-router-dom';
import { fetchUser, fetchModels, setUserModel, logoutUser } from '../api';
import styles from './ChatApp.module.css';

interface HeaderProps {
  toggleSidebar: () => void;
  activeChat: Chat | null;
  isSidebarHidden: boolean;
  balance: number; // Новый пропс для баланса
  setBalance: React.Dispatch<React.SetStateAction<number>>; // Новый пропс для обновления баланса
}

const Header: React.FC<HeaderProps> = ({ toggleSidebar, activeChat, isSidebarHidden, balance, setBalance }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [models, setModels] = useState<AIModel[]>([]);
  const [user, setUser] = useState<User | null>(null);
  const [currentModel, setCurrentModel] = useState<string>('');
  const { openModal } = useModal();
  const navigate = useNavigate();

  const toggleUserMenu = () => setIsUserMenuOpen((prev) => !prev);

  const createNewChat = () => {
    navigate('/chat/');
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const userData = await fetchUser();
        setUser({
          email: userData.email,
          balance: userData.balance,
          current_model: userData.current_model,
          is_authenticated: userData.is_authenticated,
        });
        setBalance(userData.balance); // Устанавливаем начальный баланс
        setCurrentModel(userData.current_model || '');
      } catch (error) {
        console.error('Error fetching user info:', error);
        setUser({
          email: 'guest@example.com',
          balance: 25.00,
          current_model: 'default_model',
          is_authenticated: false,
        });
        setBalance(25.00);
      }
    };
    fetchUserInfo();
  }, [setBalance]);

  useEffect(() => {
    const fetchModelsData = async () => {
      try {
        const modelsData = await fetchModels();
        setModels(modelsData);
      } catch (error) {
        console.error('Error fetching models:', error);
        setModels([]);
      }
    };
    fetchModelsData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target && !target.closest(`.${styles.headerRight}`)) setIsUserMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleModelChange = async (newModel: string) => {
    try {
      const selectedModel = models.find((m) => m.display_name === newModel);
      if (selectedModel && user?.is_authenticated) {
        const response = await setUserModel(selectedModel.system_name);
        setCurrentModel(response.current_model);
        setUser({ ...user, current_model: response.current_model });
      } else {
        setCurrentModel(newModel);
      }
    } catch (error) {
      console.error('Error setting user model:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
      setUser({
        email: 'guest@example.com',
        balance: 25.00,
        current_model: 'default_model',
        is_authenticated: false,
      });
      setBalance(25.00);
      setCurrentModel('');
      setIsUserMenuOpen(false);
      navigate('/chat/');
    } catch (error) {
      console.error('Error logging out:', error);
      setUser({
        email: 'guest@example.com',
        balance: 25.00,
        current_model: 'default_model',
        is_authenticated: false,
      });
      setBalance(25.00);
      setCurrentModel('');
      setIsUserMenuOpen(false);
      navigate('/chat/');
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <header className={styles.siteHeader} id="header">
      <div className={styles.headerInner}>
        <div className={styles.headerLeft}>
          <button
            id="expandSidebarBtn"
            className={styles.sidebarToggle}
            onClick={toggleSidebar}
          >
            <i className="fas fa-chevron-right"></i>
          </button>
          <button
            className={styles.newChatHeaderBtn}
            onClick={createNewChat}
          >
            <i className="fas fa-plus"></i>
          </button>
          <ModelSelector
            models={models}
            currentModel={currentModel || user?.current_model || 'Выберите модель'}
            onModelChange={handleModelChange}
          />
        </div>
        <div className={styles.headerCenter}>
          <h1 className={styles.currentChatTitle}>{activeChat?.title || 'Новый чат'}</h1>
        </div>
        <div className={styles.headerRight}>
          {user?.is_authenticated ? (
            <>
              <button className={styles.userBtn} onClick={toggleUserMenu}>
                <i className="fas fa-user-circle"></i>
                <span>Профиль</span>
              </button>
              {isUserMenuOpen && (
                <div id="userMenu" className={`${styles.userMenu} ${!isUserMenuOpen ? styles.userMenuHidden : ''}`}>
                  <div className={styles.userMenuHeader}>
                    <div className={styles.email}>{user?.email || 'Гость'}</div>
                    <div className="balance">Баланс: ₽{balance.toFixed(2)}</div>
                    <button className={styles.rechargeBtn} onClick={() => openModal('paymentModal')}>
                      Пополнить
                    </button>
                  </div>
                  <button className={styles.userMenuItem} onClick={handleLogout}>
                    Выйти
                  </button>
                </div>
              )}
            </>
          ) : (
            <button className={styles.userBtn} onClick={handleLoginClick}>
              <i className="fas fa-sign-in-alt"></i>
              <span>Войти</span>
            </button>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;